import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, type FormControlStatus, FormGroup, Validators } from '@angular/forms';

import moment from 'moment-timezone';
import { firstValueFrom } from 'rxjs';

import { formSignalStatus, formSignalValue } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { ProsemirrorBasicConfig } from '@core/prosemirror-legacy/webapp';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
} from '@core/user-interface-legacy';
import {
  DynamicFormRespondComponent,
  RespondSelectSingleComponent,
} from '@tellent-ask-hr/dynamic-forms-smart-ui';
import type {
  DynamicFormAnswerConfig,
  DynamicFormConfig,
  RespondFormState,
} from '@tellent-ask-hr/dynamic-forms-types';
import { PriorityService } from '@tellent-ask-hr/templates-logic';
import {
  type AskHrDynamicFormConfig,
  AskTemplateCardComponent,
  type AskTemplateFormState,
  AskTemplatesFormService,
  FormKindProviders,
} from '@tellent-ask-hr/templates-smart-ui';
import type { AskTemplate } from '@tellent-ask-hr/templates-types';
import { TicketsDataService } from '@tellent-ask-hr/tickets-logic';
import type { AskTicketPriority, CreateAskTicket } from '@tellent-ask-hr/tickets-types';

@Component({
  selector: 'ask-hr-create-ticket-dialog',
  template: `
    <rt-dialog-header
      [heading]="'tlc.ask_hr.requests.create.heading' | translate"
      [subheading]="'tlc.ask_hr.requests.create.subheading' | translate"
    >
    </rt-dialog-header>
    <rt-dialog-body layout-justify-center>
      <div class="dialog-body">
        <ask-template-card [askTemplate]="askTemplateValue()" margin-bottom-medium />
        @if (dialogData.template.prioritySelectionEnabled) {
          <input-radio-respond
            [respondConfig]="priorityConfig"
            [respondForm]="priorityControl"
            margin-bottom-medium
          />
        }
        <dynamic-form-respond
          [formConfig]="respondFormConfig()"
          (formStateChanged)="onRespondFormValue($event)"
          (formStatusChanged)="respondFormStatus.set($event)"
        />
      </div>
    </rt-dialog-body>
    <rt-dialog-footer>
      <rt-button (click)="dialogRef.close()">
        {{ 'tlc.ask_hr.requests.create.cancel' | translate }}
      </rt-button>
      <rt-button variant="primary" [disabled]="submitButtonDisabled()" (click)="onSubmit()">
        {{ 'tlc.ask_hr.requests.create.submit' | translate }}
      </rt-button>
    </rt-dialog-footer>
  `,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    TranslatePipe,
    DynamicFormRespondComponent,
    AskTemplateCardComponent,
    DialogFooterComponent,
    ButtonComponent,
    RespondSelectSingleComponent,
  ],
  styleUrls: ['./create-ticket-dialog.component.less'],
  providers: [...FormKindProviders, [ProsemirrorBasicConfig, AskTemplatesFormService]],
})
export class CreateTicketDialogComponent extends DialogComponent<{ template: AskTemplate }, void> {
  private readonly askTemplatesFormService = inject(AskTemplatesFormService);
  private readonly ticketsDataService = inject(TicketsDataService);

  protected priorityControl = new FormGroup({
    value: new FormControl<AskTicketPriority | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
  });
  protected priorityStatus = formSignalStatus(this.priorityControl);
  protected priorityConfig = inject(PriorityService).getPriorityConfig({ isDisabled: false });
  protected askTemplateForm = this.askTemplatesFormService.createAskTemplateForm(
    this.dialogData?.template,
  );
  protected defineFormState = signal<AskTemplateFormState[]>([]);
  protected respondFormStatus = signal<FormControlStatus[]>([]);
  protected respondFormConfig = computed((): DynamicFormAnswerConfig[] =>
    this.defineFormState().map(item => ({
      kind: item.kind,
      config: item.value,
      hash: item.hash,
    })),
  );
  protected askTemplateValue = formSignalValue(this.askTemplateForm);
  protected respondFormValue = signal<Array<RespondFormState<AskHrDynamicFormConfig>>>([]);
  protected submitButtonDisabled = computed((): boolean => {
    const { prioritySelectionEnabled } = this.dialogData.template;
    const respondFormValid = this.respondFormStatus().every(status => status === 'VALID');
    const isFormValid = prioritySelectionEnabled
      ? respondFormValid && this.priorityStatus() === 'VALID'
      : respondFormValid;
    return !isFormValid;
  });

  public ngOnInit(): void {
    this.defineFormState.set(
      (this.dialogData.template?.form.items ?? []).map(item =>
        this.askTemplatesFormService.askTemplateFromDto(item),
      ),
    );
  }

  public onRespondFormValue(state: Array<RespondFormState<DynamicFormConfig>>) {
    this.respondFormValue.set(<Array<RespondFormState<AskHrDynamicFormConfig>>>state);
  }

  public async onSubmit() {
    const respondFormState = this.respondFormValue();
    const result = await firstValueFrom(
      this.ticketsDataService.createTicket({
        id: this.dialogData.template.id,
        priority: this.priorityControl.value.value ?? null,
        items: respondFormState
          .filter(item => item.kind !== 'infobox')
          .map(item => {
            if (item.kind === 'date') {
              return item.value.value
                ? {
                    ...item,
                    value: {
                      ...item.value,
                      value: moment(item.value.value).format('YYYY-MM-DD'),
                    },
                  }
                : item;
            }

            return item;
          }),
      } satisfies CreateAskTicket<AskHrDynamicFormConfig>),
    );
    if (result.isSuccess) {
      this.dialogRef.close();
    }
  }
}
